import { inject, Injectable } from '@angular/core';
import { ColDef, CsvExportParams, Grid, GridApi, GridOptions } from 'ag-grid-community';

import { GridService } from 'tms-lib';
import { AccountingPackages } from '../../constants/accounting-packages';
import { PostingDocuments } from '../../constants/posting-documents';
import { SagePosting } from '../../models/sales/sage-posting';

/**
 * What the Sales Invoice Posting and Credit Note Posting screens share (invoice posting screen,
 * Nov 2026): the one-row-per-document grid, and the posting file (`Posting.csv`) that the Sage
 * packages import and "no package" downloads.
 *
 * The screens used to export their visible grid, which was one row per posting line in the file's
 * own layout - so a user's saved column order, hidden columns or sort went into the file. The file
 * is built here instead: always every posting line of the selected documents, in the order the API
 * returns them, in the default layout the old grid had (D-6, D-7). AG Grid's own CSV export writes
 * it, on a grid that is never shown, so the quoting, byte order mark, line endings and value
 * formatting are exactly what the old export wrote.
 */
@Injectable({ providedIn: 'root' })
export class DocumentPostingService {
  private readonly gridService = inject(GridService);

  /** AG Grid adds `.csv` - the same `Posting.csv` the old export wrote. */
  public readonly fileName = $localize`Posting`;

  /** The posting screen's columns - the same for every accounting package. */
  summaryColumns(documents: PostingDocuments): ColDef[] {
    return [
      ...this.gridService.checkboxRowSelect('customerAccount', $localize`Customer Account`, true),
      this.gridService.text('customerName', $localize`Customer Name`),
      // The goods code, then any delivery / extra code - the codes the document's posting-file lines carry.
      this.gridService.text('nominalCodes', $localize`Nominal Code(s)`),
      documents === PostingDocuments.creditNotes
        ? this.gridService.number('documentNo', $localize`Credit Note No`)
        : this.gridService.number('documentNo', $localize`Invoice No`),
      this.gridService.date('documentDate', $localize`Date`),
      this.gridService.text('salesOrderNos', $localize`Sales Order No(s)`),
      this.gridService.text('customerOrderRefs', $localize`Customer Order Ref(s)`),
      this.gridService.text('sellingCompanyKey', $localize`Selling Company`),
      this.gridService.numberFormat('nettTotal', $localize`Net`, { decimalPlaces: 2 }),
      this.gridService.numberFormat('taxTotal', $localize`Tax`, { decimalPlaces: 2 }),
      this.gridService.numberFormat('overallTotal', $localize`Gross`, { decimalPlaces: 2 }),
      this.gridService.text('currencyCode', $localize`Currency`),
      this.gridService.numberFormat('exchangeRate', $localize`Exchange Rate`, { decimalPlaces: 5 }),
      this.gridService.text('errorMessage', $localize`Error`),
    ];
  }

  /**
   * The old posting grid's default columns, in order: Selling Company only when the package is
   * not a Sage one, Error only on the invoice file.
   */
  fileColumns(accountingPackage: string, documents: PostingDocuments): ColDef[] {
    const columns: ColDef[] = [
      this.gridService.text('postingType', $localize`Posting type`),
      this.gridService.text('customer', $localize`Customer ID`),
      this.gridService.text('nominalCode', $localize`Nominal Code`),
      this.gridService.text('ref', $localize`Reference`),
      this.gridService.date('documentDate', $localize`Invoice Date`),
      this.gridService.text('documentNumber', $localize`Invoice Number`),
    ];
    if (!DocumentPostingService.isSagePackage(accountingPackage)) {
      columns.push(this.gridService.text('sellingCompanyKey', $localize`Selling Company`));
    }
    columns.push(
      this.gridService.text('description', $localize`Description`),
      this.gridService.text('totalValue', $localize`Total Total`),
      this.gridService.text('taxKey', $localize`Tax Key`),
      this.gridService.text('taxValue', $localize`Tax Amount`),
      this.gridService.numberFormat('exchangeRate', $localize`Currency Rate`, { decimalPlaces: 5 }),
    );
    if (documents === PostingDocuments.salesInvoices) {
      columns.push(this.gridService.text('errorMessage', $localize`Error`));
    }
    return columns;
  }

  /** Every posting line of the selected documents, in the order the API returned them. */
  linesFor(postingLines: SagePosting[], selectedIds: string[]): SagePosting[] {
    const selected = new Set(selectedIds);
    return (postingLines ?? []).filter((line: SagePosting) => selected.has(line.id));
  }

  /**
   * Whether every ticked document still has a posting line. One that has none has left PRINTED /
   * EMAILED since the screen was read (posted, credited or voided by someone else): posting it would
   * mark it POSTED with nothing in the file (fresh-context review, Nov 2026).
   */
  allHaveLines(lines: SagePosting[], selectedIds: string[]): boolean {
    const withLines = new Set(lines.map((line: SagePosting) => line.id));
    return selectedIds.every((id: string) => withLines.has(id));
  }

  /** The file's text, without the byte order mark the download adds. */
  csv(lines: SagePosting[], columnDefs: ColDef[], skipColumnHeaders: boolean): string {
    return this.withGrid(lines, columnDefs, (api: GridApi) => api.getDataAsCsv(this.params(skipColumnHeaders)) ?? '');
  }

  /** Downloads `Posting.csv`. */
  download(lines: SagePosting[], columnDefs: ColDef[], skipColumnHeaders: boolean): void {
    this.withGrid(lines, columnDefs, (api: GridApi) => api.exportDataAsCsv(this.params(skipColumnHeaders)));
  }

  /**
   * Whether the package posts straight into the accounting system. The Sage packages (and no
   * package) post by producing the file - for them the export IS the posting. Xero and Bexio push
   * over their API, so a file would be a download nobody asked for.
   */
  static postsDirect(accountingPackage: string): boolean {
    return accountingPackage === AccountingPackages.xero || accountingPackage === AccountingPackages.bexio;
  }

  static isSagePackage(accountingPackage: string): boolean {
    return (
      accountingPackage === AccountingPackages.sage ||
      accountingPackage === AccountingPackages.sage50 ||
      accountingPackage === AccountingPackages.sage200
    );
  }

  private params(skipColumnHeaders: boolean): CsvExportParams {
    return { fileName: this.fileName, skipColumnHeaders };
  }

  /**
   * A grid that is never attached to the page - the export reads the row model, not the screen. No
   * saved layout can reach it, so the file is always the default layout.
   */
  private withGrid<T>(lines: SagePosting[], columnDefs: ColDef[], use: (api: GridApi) => T): T {
    const gridOptions: GridOptions = {
      columnDefs,
      rowData: lines,
      suppressPropertyNamesCheck: true,
    };
    const grid = new Grid(document.createElement('div'), gridOptions);
    try {
      return use(gridOptions.api);
    } finally {
      grid.destroy();
    }
  }
}
